import { Navigate, Route, Routes } from 'react-router-dom';
import { LoginPage } from './pages/auth/LoginPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { ChangePasswordPage } from './pages/auth/ChangePasswordPage';
import { MyAccountPage } from './pages/account/MyAccountPage';
import { InquiryFormPage } from './pages/public/InquiryFormPage';
import { InquiriesPage } from './pages/inquiries/InquiriesPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { ReadyToFlyPage } from './pages/ready-to-fly/ReadyToFlyPage';
import { BatchesToDeparturesPage } from './pages/batches-to-departures/BatchesToDeparturesPage';
import { EntranceExamPage } from './pages/exams/EntranceExamPage';
import { FisExamPage } from './pages/exams/FisExamPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { UsersListPage } from './pages/users/UsersListPage';
import { StudentPortalLoginsPage } from './pages/students/StudentPortalLoginsPage';
import { DatabaseBackupsPage } from './pages/backups/DatabaseBackupsPage';
import { ManagementReportsPage } from './pages/management-reports/ManagementReportsPage';
import { RolesPage } from './pages/roles/RolesPage';
import { BranchesPage } from './pages/branches/BranchesPage';
import { LeadsListPage } from './pages/leads/LeadsListPage';
import { LeadDetailPage } from './pages/leads/LeadDetailPage';
import { CentralLeadDistributionPage } from './pages/leads/CentralLeadDistributionPage';
import { LeadApprovalsPage } from './pages/leads/LeadApprovalsPage';
import { FeeChangeApprovalsPage } from './pages/students/FeeChangeApprovalsPage';
import { PersonalAppointmentsPage } from './pages/leads/PersonalAppointmentsPage';
import { CompletedConsultationsPage } from './pages/consultations/CompletedConsultationsPage';
import { CampaignsPage } from './pages/email/CampaignsPage';
import { StudentsListPage } from './pages/students/StudentsListPage';
import { AlumniListPage } from './pages/alumni/AlumniListPage';
import { AlumniDetailPage } from './pages/alumni/AlumniDetailPage';
import { StudentDetailPage } from './pages/students/StudentDetailPage';
import { PaymentsPage } from './pages/payments/PaymentsPage';
import { AdmissionsPage } from './pages/admissions/AdmissionsPage';
import { TargetsPage } from './pages/targets/TargetsPage';
import { AuditLogsPage } from './pages/audit-logs/AuditLogsPage';
import { NotificationsPage } from './pages/notifications/NotificationsPage';
import { AppLayout } from './layout/AppLayout';
import { FINANCE_WORKSPACE_ROLES } from './layout/navItems';
import { ProtectedRoute, RequirePermission } from './components/ProtectedRoute';
import { PortalLayout } from './pages/portal/PortalLayout';
import { PortalDashboardPage } from './pages/portal/PortalDashboardPage';
import { StudentChangeRequestsPage } from './pages/students/StudentChangeRequestsPage';
import { ApplicationReviewsPage } from './pages/students/ApplicationReviewsPage';
import { IntakeTransfersPage } from './pages/intake-transfers/IntakeTransfersPage';
import { FinalDocumentationPage } from './pages/final-documentation/FinalDocumentationPage';
import { FinanceDashboardPage } from './pages/finance/FinanceDashboardPage';
import { FinanceStudentsPage } from './pages/finance/FinanceStudentsPage';
import { FinanceStudentPage } from './pages/finance/FinanceStudentPage';
import { PaymentConfirmationsPage } from './pages/finance/PaymentConfirmationsPage';
import { FinanceInvoicesPage } from './pages/finance/FinanceInvoicesPage';
import { FinanceReceiptsPage } from './pages/finance/FinanceReceiptsPage';
import { PaymentLedgerPage } from './pages/finance/PaymentLedgerPage';
import { FinanceReportsPage } from './pages/finance/FinanceReportsPage';
import { MasterDataLayout } from './pages/master-data/MasterDataLayout';
import { GeographyPage } from './pages/master-data/GeographyPage';
import { AcademicCatalogPage } from './pages/master-data/AcademicCatalogPage';
import { FeeStructuresPage } from './pages/master-data/FeeStructuresPage';
import { SsclSettingsPage } from './pages/master-data/SsclSettingsPage';
import { SystemEmailSettingsPage } from './pages/master-data/SystemEmailSettingsPage';
import {
  AcademicLevelsPage,
  CountriesPage,
  CurrenciesPage,
  DocumentTypesPage,
  EmailTemplatesPage,
  IntakesPage,
  LeadSourcesPage,
  LeadStatusesPage,
  PaymentCategoriesPage,
  TaskTypesPage,
  VisaStatusesPage,
  WhatsappTemplatesPage,
} from './pages/master-data/simpleLookupPages';

export const App = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/inquire" element={<InquiryFormPage />} />

    {/* Student Portal (Phase 67) — a completely separate layout/route
        tree from the staff AppLayout below; PortalLayout itself redirects
        a non-Student user away. */}
    <Route
      element={
        <ProtectedRoute>
          <PortalLayout />
        </ProtectedRoute>
      }
    >
      <Route path="/portal" element={<PortalDashboardPage />} />
    </Route>

    <Route
      element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }
    >
      <Route
        path="/"
        element={
          <RequirePermission module="dashboard" action="view">
            <DashboardPage />
          </RequirePermission>
        }
      />
      <Route
        path="/batches-to-departures"
        element={
          <RequirePermission module="ready_to_fly" action="view">
            <BatchesToDeparturesPage />
          </RequirePermission>
        }
      />
      <Route
        path="/final-documentation"
        element={
          <RequirePermission module="ready_to_fly" action="view">
            <FinalDocumentationPage />
          </RequirePermission>
        }
      />
      <Route
        path="/ready-to-fly"
        element={
          <RequirePermission module="ready_to_fly" action="view">
            <ReadyToFlyPage />
          </RequirePermission>
        }
      />
      <Route
        path="/reports"
        element={
          <RequirePermission module="reports" action="view">
            <ReportsPage />
          </RequirePermission>
        }
      />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/my-account" element={<MyAccountPage />} />
      <Route
        path="/notifications"
        element={
          <RequirePermission module="notifications" action="view">
            <NotificationsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/users"
        element={
          <RequirePermission module="users" action="view" blockRoles={['Branch Manager']}>
            <UsersListPage />
          </RequirePermission>
        }
      />
      <Route
        path="/management-reports"
        element={
          <RequirePermission module="management_reports" action="view">
            <ManagementReportsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/database-backups"
        element={
          <RequirePermission module="audit_logs" action="view" allowRoles={['Super Admin']}>
            <DatabaseBackupsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/student-logins"
        element={
          <RequirePermission module="students" action="view" allowRoles={['Super Admin']}>
            <StudentPortalLoginsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/roles"
        element={
          <RequirePermission module="roles" action="view">
            <RolesPage />
          </RequirePermission>
        }
      />
      <Route
        path="/branches"
        element={
          <RequirePermission module="branches" action="view" blockRoles={['Branch Manager']}>
            <BranchesPage />
          </RequirePermission>
        }
      />
      <Route
        path="/inquiries"
        element={
          <RequirePermission module="inquiries" action="view">
            <InquiriesPage />
          </RequirePermission>
        }
      />

      <Route
        path="/leads"
        element={
          <RequirePermission module="leads" action="view">
            <LeadsListPage />
          </RequirePermission>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <RequirePermission module="leads" action="view">
            <LeadDetailPage />
          </RequirePermission>
        }
      />
      <Route
        path="/lead-distribution"
        element={
          <RequirePermission module="leads" action="assign">
            <CentralLeadDistributionPage />
          </RequirePermission>
        }
      />
      <Route
        path="/lead-approvals"
        element={
          <RequirePermission module="leads" action="approve">
            <LeadApprovalsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/fee-change-approvals"
        element={
          <RequirePermission module="fee_changes" action="approve">
            <FeeChangeApprovalsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/personal-appointments"
        element={
          <RequirePermission module="personal_appointments" action="view">
            <PersonalAppointmentsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/completed-consultations"
        element={
          <RequirePermission module="personal_appointments" action="view">
            <CompletedConsultationsPage />
          </RequirePermission>
        }
      />

      <Route
        path="/students"
        element={
          <RequirePermission module="students" action="view">
            <StudentsListPage />
          </RequirePermission>
        }
      />
      <Route
        path="/students/:id"
        element={
          <RequirePermission module="students" action="view">
            <StudentDetailPage />
          </RequirePermission>
        }
      />
      <Route
        path="/alumni"
        element={
          <RequirePermission module="alumni" action="view">
            <AlumniListPage />
          </RequirePermission>
        }
      />
      <Route
        path="/alumni/:id"
        element={
          <RequirePermission module="alumni" action="view">
            <AlumniDetailPage />
          </RequirePermission>
        }
      />
      <Route
        path="/application-reviews"
        element={
          <RequirePermission module="students" action="update">
            <ApplicationReviewsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/student-change-requests"
        element={
          <RequirePermission module="students" action="update">
            <StudentChangeRequestsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/intake-transfers"
        element={
          <RequirePermission module="students" action="view">
            <IntakeTransfersPage />
          </RequirePermission>
        }
      />

      {/* Finance Admin/Officer use the Finance pages below instead (Phase 96/97). */}
      <Route
        path="/payments"
        element={
          <RequirePermission module="payments" action="view" blockRoles={FINANCE_WORKSPACE_ROLES}>
            <PaymentsPage />
          </RequirePermission>
        }
      />

      {/* Finance workspace (Phase 96) — finance:view; data is branch-scoped
          by the server. */}
      {(
        [
          ['/finance', <FinanceDashboardPage />],
          ['/finance/students', <FinanceStudentsPage />],
          ['/finance/students/:id', <FinanceStudentPage />],
          ['/finance/confirmations', <PaymentConfirmationsPage />],
          ['/finance/invoices', <FinanceInvoicesPage />],
          ['/finance/receipts', <FinanceReceiptsPage />],
          ['/finance/ledger', <PaymentLedgerPage />],
          ['/finance/reports', <FinanceReportsPage />],
        ] as const
      ).map(([path, page]) => (
        <Route
          key={path}
          path={path}
          element={
            <RequirePermission module="finance" action="view">
              {page}
            </RequirePermission>
          }
        />
      ))}

      <Route
        path="/admissions"
        element={
          <RequirePermission module="academic" action="view">
            <AdmissionsPage />
          </RequirePermission>
        }
      />

      <Route
        path="/campaigns"
        element={
          <RequirePermission module="email" action="view">
            <CampaignsPage />
          </RequirePermission>
        }
      />
      <Route
        path="/entrance-exam"
        element={
          <RequirePermission module="students" action="view">
            <EntranceExamPage />
          </RequirePermission>
        }
      />
      <Route
        path="/fis-exam"
        element={
          <RequirePermission module="students" action="view">
            <FisExamPage />
          </RequirePermission>
        }
      />

      <Route
        path="/targets"
        element={
          <RequirePermission module="targets" action="view">
            <TargetsPage />
          </RequirePermission>
        }
      />

      <Route
        path="/audit-logs"
        element={
          <RequirePermission module="audit_logs" action="view">
            <AuditLogsPage />
          </RequirePermission>
        }
      />

      <Route
        path="/master-data"
        element={
          <RequirePermission module="master_data" action="view" allowRoles={['Super Admin']}>
            <MasterDataLayout />
          </RequirePermission>
        }
      >
        <Route index element={<Navigate to="countries" replace />} />
        <Route path="countries" element={<CountriesPage />} />
        <Route path="geography" element={<GeographyPage />} />
        <Route path="academic-catalog" element={<AcademicCatalogPage />} />
        <Route path="fee-structures" element={<FeeStructuresPage />} />
        <Route path="academic-levels" element={<AcademicLevelsPage />} />
        <Route path="intakes" element={<IntakesPage />} />
        <Route path="currencies" element={<CurrenciesPage />} />
        <Route path="lead-sources" element={<LeadSourcesPage />} />
        <Route path="lead-statuses" element={<LeadStatusesPage />} />
        <Route path="payment-categories" element={<PaymentCategoriesPage />} />
        <Route path="sscl-exchange-rate" element={<SsclSettingsPage />} />
        <Route path="system-email" element={<SystemEmailSettingsPage />} />
        <Route path="document-types" element={<DocumentTypesPage />} />
        <Route path="visa-statuses" element={<VisaStatusesPage />} />
        <Route path="task-types" element={<TaskTypesPage />} />
        <Route path="email-templates" element={<EmailTemplatesPage />} />
        <Route path="whatsapp-templates" element={<WhatsappTemplatesPage />} />
      </Route>
    </Route>
  </Routes>
);
